import type { StorefrontProduct } from '../../api/client';
import type { ElementProps } from './elementProps';
import { boolProp } from './elementProps';

// Theme samples are presentation data, never catalog records or orderable variants.
export function sampleProducts(props: ElementProps, limit: number): StorefrontProduct[] {
  if (!boolProp(props, 'sampleContent') || !Array.isArray(props.samples)) return [];
  return props.samples.slice(0, limit).flatMap((raw, index) => {
    if (!raw || typeof raw !== 'object' || typeof raw.title !== 'string' || typeof raw.src !== 'string') return [];
    return [{ id: `sample-${index}`, slug: `sample-${index}`, title: raw.title, status: 'DRAFT',
      descriptionHtml: typeof raw.description === 'string' ? raw.description : 'Theme sample — replace with your own products.',
      variants: [{ id: `sample-${index}`, title: 'Sample', price: String(raw.price ?? '0'), inventoryQuantity: 0 }],
      images: [{ id: `sample-${index}`, src: raw.src, position: 0 }]
    }];
  });
}

// Picks a row's cards: the theme's samples in a theme preview, else the store's own, else stand-ins once it has answered.
export function catalogRows<T>(own: readonly T[] | null | undefined, loaded: boolean, samples: readonly T[], placeholders: readonly T[], preview: boolean): readonly T[] {
  if (preview && samples.length > 0) return samples;
  if (own && own.length > 0) return own;
  return loaded ? placeholders : [];
}

/** What decides the product one block draws. */
export type BlockProductInput = {
  /** The product something names for this block: its own pick, or the page's. */
  chosen: StorefrontProduct | null;
  /** True when nothing names a product for this block, so it takes one by its position instead. */
  free: boolean;
  own: readonly StorefrontProduct[] | null | undefined;
  /** True once the store's products have been read, so a missing one means the store has none there. */
  loaded: boolean;
  slot: number;
  sample: StorefrontProduct | null;
  placeholder: StorefrontProduct;
  preview: boolean;
};

// Picks one block's product: what names it, else the theme's sample in a preview, else the store's own at its position, else a stand-in.
export function blockProduct({ chosen, free, own, loaded, slot, sample, placeholder, preview }: BlockProductInput): StorefrontProduct | null {
  if (!free) return chosen;
  if (preview && sample) return sample;
  if (chosen) return chosen;
  return own?.[slot] ?? (loaded ? placeholder : null);
}
